<svelte:options
  customElement={{
    tag: "oc-row-v2",
    /*                       */
    extend: window.__components.extend(),
    props: {
      asButton: { type: "Boolean", attribute: "as-button" },
      base64Href: { type: "String", attribute: "base64-href" },
      hideDivider: { type: "Boolean", attribute: "hide-divider" },
      href: { type: "String" },
      iconLeft: { type: "String", attribute: "icon-left" },
      iconRight: { type: "String", attribute: "icon-right" },
      ocAriaLabel: { type: "String", attribute: "oc-aria-label" },
      rel: { type: "String" },
      selected: { type: "Boolean" },
      target: { type: "String" },
      variant: { type: "String" },
      headlineLevel: { type: "Number", attribute: "headline-level" },
    },
  }}
/>

<script lang="ts">
  import { InteractiveElement } from "../../../common/components/InteractiveElement";
  import { useSlots } from "../../../common/utils/useSlots.svelte";
  import type { Props } from "./RowV2.types.js";
  import DefaultStyles from "../../../common/components/DefaultStyles/DefaultStyles.svelte";

  let {
    variant,
    headlineLevel = undefined,
    hideDivider,
    selected,
    asButton,
    href,
    base64Href,
    ocAriaLabel,
    iconLeft,
    iconRight,
    rel = undefined,
    target = undefined,
  }: Props = $props();

  const Host = $host<HTMLElement>();

  const slots = useSlots(Host);

  const divider = $derived(!hideDivider && variant !== "headline");
  const interactiveBySlot = $derived(slots["primary-action"]);

  const headlineElement = $derived(headlineLevel ? `h${headlineLevel}` : "div");
</script>

{#if divider}
  <oc-divider-v1 alignment="bottom"></oc-divider-v1>
{/if}
<InteractiveElement
  {Host}
  class={`row ${selected ? "row--selected" : ""} ${interactiveBySlot ? "-interactive" : ""}`}
  {asButton}
  bind:href
  {base64Href}
  {ocAriaLabel}
  {rel}
  {target}
>
  {#snippet children(onslotchange)}
    <slot name="primary-action" {onslotchange}></slot>

    {#if !variant || variant === "container"}
      <div class="container-wrapper">
        {#if iconLeft}
          <div class="left">
            <oc-icon-v1 size={/*                 */ "100"} type={iconLeft}></oc-icon-v1>
          </div>
        {/if}
        <div class="content"><slot /></div>
        {#if iconRight}
          <div class="right">
            <oc-icon-v1 size={/*                 */ "100"} type={iconRight}></oc-icon-v1>
          </div>
        {/if}
      </div>
    {:else if variant === "image"}
      <div class="image-wrapper">
        {#if slots.image}
          <div class="image"><slot name="image" /></div>
        {/if}
        <div class="content"><slot /></div>
        {#if iconRight}
          <div class="right">
            <oc-icon-v1 size={/*                 */ "100"} type={iconRight}></oc-icon-v1>
          </div>
        {/if}
      </div>
    {:else if variant === "headline"}
      <svelte:element this={headlineElement} class="headline-wrapper"><slot /></svelte:element>
    {:else if variant === "two-columns"}
      <dl class="two-columns-wrapper">
        {#if slots["column-label"]}
          <dt class="label"><slot name="column-label" /></dt>
        {/if}
        {#if slots["column-text"]}
          <dd class="text"><slot name="column-text" /></dd>
        {/if}
      </dl>
    {/if}
  {/snippet}
</InteractiveElement>

<DefaultStyles />

<style lang="scss" global>
  @use "@otto-ec/design-tokens/component" as tokens;
  @use "@otto-ec/otto-components-utils/scss/mixins";
  @use "../../../components/floating-focus/v1/FloatingFocusV1.shared" as floating-focus;

  $image-spacing: tokens.$oc-semantic-spacing-25;
  $image-size: 40px;
  $headline-spacing-top: tokens.$oc-semantic-spacing-150;
  $headline-spacing-bottom: tokens.$oc-semantic-spacing-25;
  $spacing-x: tokens.$oc-semantic-spacing-100;
  $headline-font: tokens.$oc-semantic-font-copy-75;
  $headline-color: tokens.$oc-base-color-gray-400;

  :host {
    display: block;
    --custom-spacing-y: #{tokens.$oc-component-row-spacing-y};
    --custom-spacing-x: 0;
  }

  ::slotted([slot="primary-action"]) {
    position: absolute;
    display: block;
    inset: 0;
    cursor: pointer;
    @include floating-focus.full-bleed();
  }

  .container-wrapper {
    display: flex;
    width: 100%;
    align-items: flex-start;
    padding: var(--custom-spacing-y) var(--custom-spacing-x);
    gap: $spacing-x;

    .left,
    .right {
      line-height: 0;
    }

    .content {
      flex-grow: 1;
      text-align: left;
      min-width: 0;
    }
  }

  .image-wrapper {
    display: flex;
    width: 100%;
    align-items: center;
    gap: $spacing-x;
    .image {
      padding: $image-spacing 0;
      width: $image-size;
      height: $image-size;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .content {
      flex-grow: 1;
      text-align: left;
      padding: tokens.$oc-component-row-spacing-y 0;
    }

    .right {
      line-height: 0;
      padding: tokens.$oc-component-row-spacing-y 0;
    }
  }

  .headline-wrapper {
    width: 100%;
    margin: 0;
    padding: $headline-spacing-top 0 $headline-spacing-bottom;
    font: $headline-font;
    color: $headline-color;
    text-transform: uppercase;
  }

  .two-columns-wrapper {
    margin: 0;
    width: 100%;
    display: flex;
    gap: $spacing-x;
    padding: tokens.$oc-component-row-spacing-y 0;

    .label {
      font-weight: 700;
      flex: 1;
    }

    .text {
      margin: 0;
      flex: 1;
    }
  }

  .row {
    display: block;
    position: relative;
    width: 100%;
    padding: 0;
    margin: 0;
    text-decoration: none;
    text-align: left;
    border: none;
    background-color: tokens.$oc-component-row-background-color;
    font: tokens.$oc-semantic-font-copy-100;
    color: tokens.$oc-semantic-color-text-default;
    outline: none;
    outline-offset: 0;

    &--divider {
      margin-top: calc(-0.5 * tokens.$oc-component-row-border-width);
      border-top: tokens.$oc-component-row-border-width solid tokens.$oc-component-row-border-color;
    }

    &--selected {
      background-color: tokens.$oc-component-row-background-color-selected;
      font-weight: 700;
    }

    &.-interactive {
      cursor: pointer;

      @media (hover: hover) {
        &:hover {
          background-color: tokens.$oc-component-row-background-color-hover;
        }
      }

      &:active {
        background-color: tokens.$oc-component-row-background-color-active;
      }
    }

    &.row.-interactive.row--selected {
      @media (hover: hover) {
        &:hover {
          background-color: tokens.$oc-component-row-background-color-selected-hover;
        }
      }

      &:active {
        background-color: tokens.$oc-component-row-background-color-selected-active;
      }
    }
  }
</style>
